import jsPDF from 'jspdf';
import type { IMedicalRecord } from '../interfaces/IMedicalRecord';
import type { IUser } from '../interfaces/IUser';
import logo from '../assets/logo5.png';
import {
  NAVY, GRAY, LGRAY, BLACK,
  v, fmt, hhmm, f,
  secBox, secBar, colHead, ul, field, multiLine, examCell,
} from './pdfHelpers';

// ─────────────────────────────────────────────────────────────────────────────
export async function generateMedicalRecordPDF(
  record: IMedicalRecord,
  patientData?: IUser | null,
): Promise<void> {
  const doc = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });

  const LX = 13;   // margem esquerda
  const W  = 184;  // largura útil

  // ── CABEÇALHO ────────────────────────────────────────────────────────────────
  let y = 8;
  doc.addImage(logo,'PNG', (210-50)/2, y, 50, 16);
  y += 18;

  ul(doc, LX, y, W); y += 4;

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6.5);
  doc.text('Nº PRONTUÁRIO:', LX, y);
  f(doc,'text',BLACK); doc.setFontSize(7);
  doc.text(record.id.slice(0,8).toUpperCase(), LX+22, y);

  f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(11);
  doc.text('PRONTUÁRIO MÉDICO OFTALMOLÓGICO', 105, y, { align:'center' });

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6.5);
  doc.text('DATA:', LX+W-28, y);
  f(doc,'text',BLACK); doc.setFontSize(7);
  doc.text(fmt(record.createdAt), LX+W-18, y);

  y += 2; ul(doc, LX, y, W); y += 3;

  // Layout vertical — tudo calculado para caber em A4 (297 mm):
  //   cabeçalho ≈ 35 mm  |  S1=42  S2=38  S3=76  S4=45  S5=35  |  rodapé ≈ 14 mm
  //   total ≈ 291 mm → dentro dos 297 mm

  // ── SEÇÃO 1 — Identificação do Paciente ──────────────────────────────────────
  const S1_Y = y; const S1_H = 42;
  secBox(doc, LX, S1_Y, W, S1_H);
  secBar(doc, LX, S1_Y, W, '  1. IDENTIFICAÇÃO DO PACIENTE');

  const p = patientData?.profile;
  let r1 = S1_Y + 10;  // 3,5 mm de respiro após a barra

  field(doc, 'Nome:',              v(patientData?.name ?? record.patient?.name), LX+2,   r1, 112);
  field(doc, 'Data de Nascimento:', fmt(p?.birthDay),                             LX+117, r1,  66); r1+=7;

  field(doc, 'CPF:',      v(p?.registrationNumber), LX+2,   r1, 48);
  field(doc, 'Telefone:', v(p?.phone),               LX+53,  r1, 50);
  field(doc, 'E-mail:',   v(patientData?.email),     LX+106, r1, 76); r1+=7;

  field(doc, 'Endereço:', v(p?.address), LX+2, r1, W-4); r1+=7;

  field(doc, 'Cidade:', v(p?.city),     LX+2,  r1, 72);
  field(doc, 'UF:',     v(p?.state),    LX+77, r1, 27);
  field(doc, 'CEP:',    v(p?.zip_code), LX+107,r1, 44); r1+=7;

  field(doc, 'Convênio/Plano:', v(record.insurancePlan), LX+2, r1, W-4);

  // ── SEÇÃO 2 — Dados da Consulta ───────────────────────────────────────────────
  const S2_Y = S1_Y+S1_H+3; const S2_H = 38;
  secBox(doc, LX, S2_Y, W, S2_H);
  secBar(doc, LX, S2_Y, W, '  2. DADOS DA CONSULTA');

  let r2 = S2_Y+10;
  const apptStart = record.appointment?.startTime ?? record.createdAt;
  field(doc, 'Data da Consulta:', fmt(apptStart),               LX+2,   r2, 50);
  field(doc, 'Hora:',             hhmm(apptStart),               LX+55,  r2, 24);
  field(doc, 'Médico:',           v(record.doctor?.user?.name),  LX+82,  r2, 55);
  field(doc, 'Especialidade:',    'Oftalmologia',                 LX+140, r2, 44); r2+=7;

  multiLine(doc, 'Queixa Principal:',          v(record.chiefComplaint),         LX+2, r2, W-4, 1); r2+=7;
  multiLine(doc, 'História da Moléstia Atual:', v(record.currentIllnessHistory),  LX+2, r2, W-4, 2);

  // ── SEÇÃO 3 — Exame Oftalmológico ─────────────────────────────────────────────
  // Referência de posições (r3=S3_Y+10, r3+=7 após cada colHead):
  //   Horiz A/B em +33  |  Horiz B/C em +55  |  Bloco C começa em +58
  const S3_Y = S2_Y+S2_H+3; const S3_H = 76;
  secBox(doc, LX, S3_Y, W, S3_H);
  secBar(doc, LX, S3_Y, W, '  3. EXAME OFTALMOLÓGICO');

  const C1X = LX+2;  const C1W = 59;
  const C2X = LX+63; const C2W = 62;
  const C3X = LX+127;const C3W = 57;

  // divisórias verticais
  f(doc,'stroke',LGRAY); doc.setLineWidth(0.2);
  doc.line(C2X-1.5, S3_Y+7, C2X-1.5, S3_Y+S3_H-1);
  // C3X apenas nos blocos A e C; bloco B usa biomicroscopia estendida
  doc.line(C3X-1.5, S3_Y+7,  C3X-1.5, S3_Y+33);
  doc.line(C3X-1.5, S3_Y+55, C3X-1.5, S3_Y+S3_H-1);

  const o = record.ophthalmologyRecord;
  let r3 = S3_Y+10;

  // ─ bloco A ───────────────────────────────────────────────────────────────────
  colHead(doc, C1X, r3, C1W, 'ACUIDADE VISUAL');
  colHead(doc, C2X, r3, C2W, 'REFRAÇÃO');
  colHead(doc, C3X, r3, C3W, 'FUNDOSCOPIA');
  r3 += 7;  // 2 mm de respiro após o cabeçalho

  f(doc,'text',GRAY); doc.setFont('helvetica','bold'); doc.setFontSize(6);
  doc.text('SC',  C1X+15, r3, { align:'center' });
  doc.text('CC',  C1X+27, r3, { align:'center' });
  doc.text('P/H', C1X+39, r3, { align:'center' });
  ['ESF','CIL','EIXO','ADD','DNP'].forEach((l,i) =>
    doc.text(l, C2X+9.5+i*11.5, r3, { align:'center' }));
  r3 += 4;

  // linha OD
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(7);
  doc.text('OD', C1X, r3);
  [[C1X+10,v(o?.visualAcuityOdSc)],[C1X+22,v(o?.visualAcuityOdCc)],[C1X+34,v(o?.visualAcuityOdPh)]].forEach(([x,val]) => {
    ul(doc, x as number, r3+0.8, 10);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val as string, x as number, r3); }
  });
  doc.text('OD', C2X, r3); f(doc,'text',GRAY);
  [v(o?.refractionOdEsf),v(o?.refractionOdCil),v(o?.refractionOdAxis),v(o?.refractionOdAdd),v(o?.refractionOdDnp)].forEach((val,i) => {
    ul(doc, C2X+5+i*11.5, r3+0.8, 9);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val, C2X+5+i*11.5, r3); f(doc,'text',GRAY); }
  });
  examCell(doc, 'OD:', v(o?.fundoscopyOd), C3X, r3, C3W-2);
  r3 += 5;

  // linha OE
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(7);
  doc.text('OE', C1X, r3);
  [[C1X+10,v(o?.visualAcuityOeSc)],[C1X+22,v(o?.visualAcuityOeCc)],[C1X+34,v(o?.visualAcuityOePh)]].forEach(([x,val]) => {
    ul(doc, x as number, r3+0.8, 10);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val as string, x as number, r3); }
  });
  doc.text('OE', C2X, r3); f(doc,'text',GRAY);
  [v(o?.refractionOeEsf),v(o?.refractionOeCil),v(o?.refractionOeAxis),v(o?.refractionOeAdd),v(o?.refractionOeDnp)].forEach((val,i) => {
    ul(doc, C2X+5+i*11.5, r3+0.8, 9);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val, C2X+5+i*11.5, r3); f(doc,'text',GRAY); }
  });
  examCell(doc, 'OE:', v(o?.fundoscopyOe), C3X, r3, C3W-2);
  r3 += 7;

  // divisória horizontal A/B  (r3 = S3_Y+33)
  f(doc,'stroke',LGRAY); doc.setLineWidth(0.2);
  doc.line(LX+1, r3, LX+W-1, r3); r3 += 3;

  // ─ bloco B ───────────────────────────────────────────────────────────────────
  const bioW = C3X - C2X + C3W; // biomicroscopia ocupa C2 + C3
  colHead(doc, C1X, r3, C1W, 'TONOMETRIA (mmHg)');
  colHead(doc, C2X, r3, bioW, 'BIOMICROSCOPIA');
  r3 += 7;

  examCell(doc, 'OD:', v(o?.tonometryOd),    C1X, r3, C1W-2);
  examCell(doc, 'OD:', v(o?.biomicroscopyOd), C2X, r3, bioW-2);
  r3 += 5;
  examCell(doc, 'OE:', v(o?.tonometryOe),    C1X, r3, C1W-2);
  examCell(doc, 'OE:', v(o?.biomicroscopyOe), C2X, r3, bioW-2);
  r3 += 7;

  // divisória horizontal B/C  (r3 = S3_Y+55)
  f(doc,'stroke',LGRAY); doc.setLineWidth(0.2);
  doc.line(LX+1, r3, LX+W-1, r3); r3 += 3;

  // ─ bloco C ───────────────────────────────────────────────────────────────────
  colHead(doc, C1X, r3, C1W, 'CAMPIMETRIA');
  colHead(doc, C2X, r3, C2W, 'PAQUIMETRIA (μm)');
  colHead(doc, C3X, r3, C3W, 'OUTROS EXAMES');
  r3 += 7;

  examCell(doc, 'OD:', v(o?.campimetryOd), C1X,    r3, 26);
  examCell(doc, 'OE:', v(o?.campimetryOe), C1X+28, r3, 26);

  const pkOd = o?.pachymetryOd != null ? String(o.pachymetryOd) : '';
  const pkOe = o?.pachymetryOe != null ? String(o.pachymetryOe) : '';
  examCell(doc, 'OD:', pkOd, C2X,    r3, 28);
  examCell(doc, 'OE:', pkOe, C2X+30, r3, 28);

  const others = doc.splitTextToSize(v(o?.otherExams), C3W-2);
  others.slice(0,3).forEach((line: string, i: number) => {
    ul(doc, C3X, r3+0.8+i*4, C3W-2);
    if (line) { f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.text(line, C3X, r3+i*4); }
  });

  // ── SEÇÃO 4 — Avaliação e Conduta ─────────────────────────────────────────────
  const S4_Y = S3_Y+S3_H+3; const S4_H = 45;
  secBox(doc, LX, S4_Y, W, S4_H);
  secBar(doc, LX, S4_Y, W, '  4. AVALIAÇÃO E CONDUTA');

  const hW = (W-6)/2;
  let r4 = S4_Y+10;

  f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(6.5);
  doc.text('HIPÓTESES DIAGNÓSTICAS / CID:', LX+2, r4);
  doc.text('CONDUTA / PLANO TERAPÊUTICO:',  LX+4+hW, r4); r4+=4;

  const diagVal = [(record.cidCodes??[]).join(', '), v(record.diagnosis)].filter(Boolean).join(' — ');
  const dLines  = doc.splitTextToSize(diagVal, hW-2);
  const cLines  = doc.splitTextToSize(v(record.therapeuticPlan), hW-2);

  for (let i=0; i<2; i++) {
    ul(doc, LX+2,    r4+0.8+i*5.5, hW);
    ul(doc, LX+4+hW, r4+0.8+i*5.5, hW);
    f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(7.5);
    if (dLines[i]) doc.text(dLines[i], LX+2,    r4+i*5.5);
    if (cLines[i]) doc.text(cLines[i], LX+4+hW, r4+i*5.5);
  }
  r4 += 16;

  f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(6.5);
  doc.text('PRESCRIÇÃO:', LX+2, r4); r4+=4;

  const pLines = doc.splitTextToSize(v(record.prescription), W-6);
  for (let i=0; i<2; i++) {
    ul(doc, LX+2, r4+0.8+i*5.5, W-6);
    if (pLines[i]) { f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(7.5); doc.text(pLines[i], LX+2, r4+i*5.5); }
  }

  // ── SEÇÃO 5 — Retorno ─────────────────────────────────────────────────────────
  const S5_Y = S4_Y+S4_H+3; const S5_H = 35;
  secBox(doc, LX, S5_Y, W, S5_H);
  secBar(doc, LX, S5_Y, W, '  5. RETORNO');

  const retW = W*0.56;
  const sigX = LX+retW+6;
  const sigW = W-retW-8;
  let r5 = S5_Y+10;

  field(doc, 'Data do Retorno:', fmt(record.returnDate), LX+2, r5, retW); r5+=7;
  multiLine(doc, 'Observações:', v(record.observations), LX+2, r5, retW, 2);

  // assinatura (coluna direita)
  const sigY = S5_Y+S5_H-13;
  ul(doc, sigX, sigY, sigW);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6.5);
  doc.text('Assinatura e Carimbo do Médico', sigX+sigW/2, sigY+3, { align:'center' });
  examCell(doc, 'CRM:', v(record.doctor?.CRM), sigX, sigY+9, sigW);

  // ── RODAPÉ ────────────────────────────────────────────────────────────────────
  const FY = S5_Y+S5_H+4;
  ul(doc, LX, FY, W);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6);
  doc.text(
    'Rua Exemplo, 123 - Centro - Sua Cidade/UF - CEP 00000-000     (00) 0000-0000     www.sagradavisao.com.br',
    105, FY+4, { align:'center' },
  );

  // ── SALVAR ────────────────────────────────────────────────────────────────────
  const name     = v(patientData?.name ?? record.patient?.name ?? 'paciente');
  const datePart = fmt(record.createdAt).replace(/\//g,'-');
  doc.save(`prontuario_${name.replace(/\s+/g,'_').toLowerCase()}_${datePart}.pdf`);
}
